import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { CALCULATORS } from "@/data/calculators";
import { API } from "@/lib/api";
import { resetConsent } from "@/lib/consent";
import { nf } from "@/lib/format";

export function Footer() {
  const [total, setTotal] = useState(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    fetch(`${API}/stats`)
      .then((r) => r.json())
      .then((d) => setTotal(d.total))
      .catch(() => {});
  }, []);

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Escribe un email válido para apuntarte.");
      return;
    }
    toast.success("Suscripción confirmada. Bienvenida la curiosidad.");
    setEmail("");
  };

  const resetCookies = () => {
    resetConsent();
    toast.info("Preferencias borradas: vuelve a decidir cuando quieras.");
  };

  return (
    <footer className="border-t border-border bg-secondary/40">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <Logo className="h-8 w-8" />
              <span className="font-serif text-xl font-semibold">Curiosímetro</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              Calculadoras insólitas del cuerpo humano y la vida cotidiana. Números que
              nadie te enseña a mirar.
            </p>
            {total !== null && (
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-ink-muted" data-testid="footer-total-calculations">
                {nf(total)} cálculos realizados aquí
              </p>
            )}
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-ink-muted">Calculadoras</h3>
            <ul className="mt-4 space-y-2.5">
              {CALCULATORS.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/${c.slug}`}
                    data-testid={`footer-link-${c.slug}`}
                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    {c.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-ink-muted">Curiosidad semanal</h3>
            <p className="mt-4 text-sm text-muted-foreground">
              Una cifra asombrosa sobre tu cuerpo cada lunes. Sin ruido.
            </p>
            <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                data-testid="newsletter-email-input"
                className="h-10 flex-1 rounded-md border border-input bg-card px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-ring/40"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-button"
                className="flex h-10 items-center gap-1.5 rounded-md bg-foreground px-4 text-sm font-medium text-background transition-all hover:bg-primary hover:text-primary-foreground"
              >
                Apuntarme <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 select-none overflow-hidden" aria-hidden="true">
          <p className="whitespace-nowrap text-center font-serif text-[13vw] font-black leading-none tracking-tight text-foreground/[0.07]">
            CURIOSÍMETRO
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-border pt-6 text-xs text-ink-muted sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <p>© {new Date().getFullYear()} Curiosímetro. Hecho con curiosidad.</p>
            <button
              onClick={resetCookies}
              data-testid="cookie-preferences-button"
              className="underline decoration-dotted underline-offset-2 transition-colors hover:text-primary"
            >
              Preferencias de cookies
            </button>
            <Link
              to="/privacidad"
              data-testid="footer-link-privacidad"
              className="underline decoration-dotted underline-offset-2 transition-colors hover:text-primary"
            >
              Privacidad
            </Link>
            <Link
              to="/metodologia"
              data-testid="footer-link-metodologia"
              className="underline decoration-dotted underline-offset-2 transition-colors hover:text-primary"
            >
              Metodología
            </Link>
          </div>
          <p className="max-w-xl sm:text-right">
            Los resultados son estimaciones divulgativas basadas en medias científicas.
            No constituyen consejo médico.
          </p>
        </div>
      </div>
    </footer>
  );
}
